'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect } from 'react';
import { AppType, wait } from '@insurgrid/common';
import {
  useAgentInfo,
  useAnalytics,
  useEventAnalytics,
  useLandingTelemetry,
} from '../../util/hooks';
import { useFeatureFlag } from '../../util/hooks/useFeatureFlag';
import { LandingViewTemplate } from '../LandingViewTemplate';
import {
  useAppTypeContext,
  useDemoContext,
  useFeatureContext,
} from '../../contexts';
import { Feature } from '../../services/feature.service';
import { useSubmissionNav } from '../resources/submissions/hooks/useSubmissionNav';
import { Loading as LoadingComponent } from '../Loading';
import { useReturnToStep } from '../resources/navigationHooks/useReturnToStep';
import { resolveContinueDestination } from './continueDestination';

export function LandingView() {
  useAnalytics('Landing Page', {
    path: '/landing',
    url: `${window.location.origin}/`,
  });
  const router = useRouter();
  const { agentInfo, agentError } = useAgentInfo();
  const { captureEvent } = useEventAnalytics();
  const { submissions, isLoading } = useReturnToStep();
  useLandingTelemetry(!!isLoading, !!agentError, captureEvent);
  const { overview, carrierSelect, intakeForm } = useSubmissionNav();
  const nextSection = submissions?.length ? overview : carrierSelect;
  const showIntakeForm = useFeatureFlag(agentInfo?.slug, 'show_intake_form');
  const features = useFeatureContext();
  const agentName = agentInfo?.agency?.name;
  const appType = useAppTypeContext();
  const { setStepIndex, setStepCompleted } = useDemoContext();
  const useNewCopy = !!features?.[Feature.LANDING_PAGE_COPY_JAN_23];

  useEffect(() => {
    setStepIndex(0);
  }, []);

  if (isLoading) return <LoadingComponent />;

  return (
    <LandingViewTemplate
      agencyName={agentName ?? null}
      titleUseCase={
        useNewCopy ? 'verify your insurance' : 'securely check your insurance'
      }
      iconOneHeading="Connect effortlessly"
      iconOneDescription="InsurGrid lets you securely connect your insurance accounts in seconds"
      iconTwoHeading={useNewCopy ? 'Your data belongs to you' : undefined}
      iconTwoDescription={
        useNewCopy
          ? 'InsurGrid does not sell personal info, and we will only use it with your permission'
          : undefined
      }
      buttonColorCode={`#${agentInfo?.agency?.colorCode}`}
      onContinueClick={async () => {
        if (appType === AppType.Demo) {
          // carrier select
          setStepCompleted(true);
          await wait(400);
          router.replace(carrierSelect.path);
          return;
        }

        const destination = resolveContinueDestination(
          !!submissions?.length,
          !!showIntakeForm,
        );
        captureEvent('Clicked Continue Landing Page', { destination });
        if (destination === 'overview') {
          await overview.go();
        } else if (destination === 'intakeForm') {
          await intakeForm.go();
        } else {
          await carrierSelect.go();
        }
      }}
    >
      <div
        className="text-sm text-gray-500 sm:flex mt-4 mb-2 md:mb-0 pb-2 w-full"
        onClick={() => {
          captureEvent('Privacy Policy Clicked', {
            userId: agentInfo?.externalIdentifier,
            email: agentInfo?.email,
          });
        }}
        role="button"
      >
        <p className={'w-full text-center'}>
          By selecting "Continue", you agree to the{' '}
          <a
            className="font-medium underline text-gray-600 whitespace-nowrap"
            href="https://www.insurgrid.com/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            InsurGrid Privacy Policy.
          </a>
        </p>
      </div>
    </LandingViewTemplate>
  );
}
